import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Paper, Box, Typography, InputAdornment } from '@mui/material';
import { fetchCuratedFonts, loadGoogleFont, getFontCategoryLabel } from 'utils/googleFontsApi';

const TeamChooseFontField = ({ selectedFont = 'Inter', onFontChange }) => {
    const [fonts, setFonts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isUserTyping, setIsUserTyping] = useState(false);

    // Fetch fonts on mount
    useEffect(() => {
        const loadFonts = async () => {
            try {
                setLoading(true);
                const fontList = await fetchCuratedFonts();
                setFonts(fontList);

                // Preload the selected font
                if (selectedFont) {
                    await loadGoogleFont(selectedFont, ['400', '500', '600', '700']).catch(err => {
                        console.warn('Failed to preload selected font:', err);
                    });
                }

                // Preload all fonts in the background for dropdown display
                // Load them in batches to avoid overwhelming the browser
                const preloadAllFonts = async () => {
                    const batchSize = 5;
                    for (let i = 0; i < fontList.length; i += batchSize) {
                        const batch = fontList.slice(i, i + batchSize);
                        await Promise.all(
                            batch.map(font =>
                                loadGoogleFont(font.family, font.variants || ['400', '500', '600', '700'])
                                    .catch(err => console.warn(`Failed to preload font ${font.family}:`, err))
                            )
                        );
                        // Small delay between batches to avoid blocking
                        if (i + batchSize < fontList.length) {
                            await new Promise(resolve => setTimeout(resolve, 50));
                        }
                    }
                };

                // Start preloading in the background (don't wait for it)
                preloadAllFonts().catch(err => console.warn('Error preloading fonts:', err));
            } catch (error) {
                console.error('Error fetching fonts:', error);
            } finally {
                setLoading(false);
            }
        };

        loadFonts();
    }, [selectedFont]);

    // Handle font selection
    const handleFontChange = (event, newValue) => {
        if (newValue && onFontChange) {
            const fontFamily = newValue.family;
            onFontChange(fontFamily);

            // Update input value to show selected font
            setInputValue(fontFamily);

            // Load the selected font
            loadGoogleFont(fontFamily, ['400', '500', '600', '700']).catch(err => {
                console.warn('Failed to load font:', err);
            });
        } else if (newValue === null) {
            // Handle clear action - reset to default
            setInputValue('');
        }
    };

    // Find the selected font object
    const selectedFontObject = fonts.find(f => f.family === selectedFont) || null;

    // Update input value when selectedFont changes and ensure font is loaded
    useEffect(() => {
        if (selectedFontObject) {
            setInputValue(selectedFontObject.family);
            // Ensure the font is loaded for preview
            loadGoogleFont(selectedFontObject.family, ['400', '500', '600', '700']).catch(err => {
                console.warn('Failed to load font for preview:', err);
            });
        }
    }, [selectedFontObject]);

    // Custom option renderer
    const renderOption = (props, option) => {
        const fontFamily = option.family;
        // Use the exact font family name with proper fallbacks
        const fontStyle = {
            fontFamily: `"${fontFamily}", ${option.category || 'sans-serif'}`,
            // Force the browser to use the font
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale'
        };

        return (
            <Box component="li" {...props} key={option.family}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 0.5 }}>
                    <Typography
                        component="span"
                        sx={{
                            ...fontStyle,
                            fontSize: '1.2rem',
                            fontWeight: 600,
                            minWidth: '40px',
                            textAlign: 'center',
                            display: 'inline-block',
                            lineHeight: 1
                        }}
                    >
                        Aa
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{
                                ...fontStyle,
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                display: 'block'
                            }}
                        >
                            {fontFamily}
                        </Typography>
                        <Typography
                            component="span"
                            variant="caption"
                            sx={{
                                ...fontStyle,
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mt: 0.25
                            }}
                        >
                            The quick brown fox jumps over the lazy dog
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };


    return (
        <Box sx={{ marginTop: 3 }}>
            {/* Label positioned outside and left-aligned */}
            <Typography
                variant="body2"
                sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: "#4f4f4f",
                    marginBottom: "10px",
                    textAlign: 'left'
                }}
            >
                Choose Font
            </Typography>

            {/* Container with colored background */}
            <Box sx={{ backgroundColor: "#f7f7f7", padding: 2 }}>
                {/* Inner Box with White Background and Border */}
                <Box
                    sx={{
                        backgroundColor: "#ffffff",
                        border: '1px solid #dbdbdb',
                        borderRadius: "6px",
                        overflow: 'hidden'
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Autocomplete
                            options={fonts}
                            getOptionLabel={(option) => option.family}
                            value={selectedFontObject}
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'input') {
                                    // User is actively typing
                                    setIsUserTyping(true);
                                    setInputValue(newInputValue);
                                    setSearchQuery(newInputValue);
                                    setOpen(true);
                                } else if (reason === 'clear') {
                                    // User cleared input
                                    setIsUserTyping(true);
                                    setInputValue('');
                                    setSearchQuery('');
                                    setOpen(true);
                                } else {
                                    // Other reasons (reset, etc.) - not user typing
                                    setIsUserTyping(false);
                                }
                            }}
                            onChange={handleFontChange}
                            open={open}
                            onOpen={() => {
                                // When opening, mark that user is not typing (yet)
                                setIsUserTyping(false);
                                setOpen(true);

                                // Ensure all fonts are loaded when dropdown opens
                                // This helps if some fonts weren't preloaded yet
                                fonts.forEach(font => {
                                    loadGoogleFont(font.family, font.variants || ['400', '500', '600', '700'])
                                        .catch(err => console.warn(`Failed to load font ${font.family}:`, err));
                                });
                            }}
                            onClose={(event, reason) => {
                                // Reset input value to show selected font when closing
                                if (selectedFontObject) {
                                    setInputValue(selectedFontObject.family);
                                }
                                setIsUserTyping(false);
                                setOpen(false);
                            }}
                            filterOptions={(options, state) => {
                                // Always show all options - we'll handle filtering differently
                                // The isUserTyping flag should prevent filtering when just opening
                                if (!isUserTyping) {
                                    return options;
                                }

                                // If input is empty, show all options
                                if (!state.inputValue || state.inputValue.trim() === '') {
                                    return options;
                                }

                                // User is actively typing - filter the options
                                const input = state.inputValue.toLowerCase();
                                const filtered = options.filter(option =>
                                    option.family.toLowerCase().includes(input)
                                );

                                // If filtered result is empty or only one match, return all to avoid confusion
                                if (filtered.length === 0) {
                                    return options;
                                }

                                return filtered;
                            }}
                            openOnFocus
                            disableClearable={false}
                            renderOption={renderOption}
                            renderInput={(params) => {
                                const selectedFontStyle = selectedFontObject
                                    ? { fontFamily: `"${selectedFontObject.family}", sans-serif` }
                                    : {};

                                return (
                                    <TextField
                                        {...params}
                                        placeholder="Select a font"
                                        variant="outlined"
                                        onClick={() => {
                                            if (!open) {
                                                setIsUserTyping(false);
                                                setOpen(true);
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {selectedFontObject && !isUserTyping && (
                                                        <InputAdornment position="start" sx={{ mr: 1, ml: 0.5 }}>
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    ...selectedFontStyle,
                                                                    fontSize: '1.2rem',
                                                                    fontWeight: 600,
                                                                    minWidth: '32px',
                                                                    textAlign: 'center',
                                                                    lineHeight: 1,
                                                                    display: 'inline-block',
                                                                    color: '#333'
                                                                }}
                                                            >
                                                                Aa
                                                            </Box>
                                                        </InputAdornment>
                                                    )}
                                                    {params.InputProps?.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            style: {
                                                ...selectedFontStyle,
                                                fontWeight: 500,
                                                fontSize: '0.95rem'
                                            }
                                        }}
                                    />
                                );
                            }}
                            componentsProps={{
                                popper: {
                                    onClick: (e) => {
                                        // Prevent closing when clicking inside the popper
                                        e.stopPropagation();
                                    }
                                },
                                popupIndicator: {
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        if (!open) {
                                            // Opening - ensure we show all fonts
                                            setIsUserTyping(false);
                                            setOpen(true);
                                        } else {
                                            // Closing
                                            setOpen(false);
                                        }
                                    }
                                }
                            }}
                            PaperComponent={({ children, ...other }) => (
                                <Paper {...other} sx={{ maxHeight: 300 }}>
                                    {children}
                                </Paper>
                            )}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    padding: '8px 14px',
                                    '& fieldset': {
                                        border: 'none'
                                    },
                                    '& .MuiAutocomplete-endAdornment': {
                                        right: '8px'
                                    }
                                }
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default TeamChooseFontField;